import { useCallback, useEffect, useRef, useState } from "react";
import { apiClient } from "../lib/api";

/**
 * useKaizenLiveOffers — the ONE data path for Kaizen intake.
 *
 * Every surface that shows Kaizen offers (KaizenPanel settings screen, The Floor
 * incoming-offers lane, and any future dashboard widget) consumes this hook so
 * accept / decline / suggested-providers all flow through a single logic
 * boundary. Adding a new surface never re-implements the fetch, the polling
 * cadence, the auth path, or the accept semantics.
 *
 * Endpoints used (all pre-existing):
 *   GET  /kaizen/live-offers                              — visible=true offers
 *   POST /kaizen/offers/{ride_id}/accept?auto_assign=…    — Playwright accept + local trip
 *   POST /kaizen/offers/{ride_id}/decline                 — Playwright decline
 *   GET  /kaizen/offers/{ride_id}/suggested-providers     — Nova ranker
 *
 * Options:
 *   pollMs                    cadence (default 5000). Pass 0 to disable polling.
 *   onOfferArrival(offer)     fires once per newly-observed ride_id — used by
 *                             The Floor to whisper "kaizen offer arrived".
 */
export function useKaizenLiveOffers({ pollMs = 5000, onOfferArrival } = {}) {
  const [offers, setOffers] = useState([]);
  const [fetchedAt, setFetchedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({}); // { [rideId]: "accept" | "decline" }
  const [suggestions, setSuggestions] = useState({}); // { [rideId]: [{...}] }
  const seenIdsRef = useRef(new Set());
  const arrivalRef = useRef(onOfferArrival);
  useEffect(() => { arrivalRef.current = onOfferArrival; }, [onOfferArrival]);

  const load = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/kaizen/live-offers");
      const rows = Array.isArray(data) ? data : [];
      setOffers(rows);
      setFetchedAt(new Date());
      // Detect new arrivals so subscribers can react (whisper, sound, badge).
      const next = new Set();
      rows.forEach((o) => {
        const rid = offerRideId(o);
        if (!rid) return;
        next.add(rid);
        if (!seenIdsRef.current.has(rid) && arrivalRef.current) {
          try { arrivalRef.current(o); } catch { /* noop */ }
        }
      });
      seenIdsRef.current = next;
    } catch (_e) {
      // Silent — polling picks up recoveries. The panel that owns the UI can
      // show a banner if it cares about the auth-lost case.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    if (!pollMs) return undefined;
    const t = setInterval(load, pollMs);
    return () => clearInterval(t);
  }, [load, pollMs]);

  const accept = useCallback(async (rideId, { autoAssign = false } = {}) => {
    if (!rideId || busy[rideId]) return { ok: false };
    setBusy((p) => ({ ...p, [rideId]: "accept" }));
    try {
      const qs = autoAssign ? "?auto_assign=true" : "";
      const { data } = await apiClient.post(`/kaizen/offers/${rideId}/accept${qs}`);
      // Optimistically drop it — next poll confirms.
      if (data?.ok) setOffers((list) => list.filter((o) => offerRideId(o) !== rideId));
      return data || { ok: false };
    } catch (e) {
      return { ok: false, error: e?.response?.data?.detail || String(e) };
    } finally {
      setBusy((p) => {
        const { [rideId]: _drop, ...rest } = p;
        return rest;
      });
    }
  }, [busy]);

  const decline = useCallback(async (rideId) => {
    if (!rideId || busy[rideId]) return { ok: false };
    setBusy((p) => ({ ...p, [rideId]: "decline" }));
    try {
      const { data } = await apiClient.post(`/kaizen/offers/${rideId}/decline`);
      if (data?.ok) setOffers((list) => list.filter((o) => offerRideId(o) !== rideId));
      return data || { ok: false };
    } catch (e) {
      return { ok: false, error: e?.response?.data?.detail || String(e) };
    } finally {
      setBusy((p) => {
        const { [rideId]: _drop, ...rest } = p;
        return rest;
      });
    }
  }, [busy]);

  const loadSuggestions = useCallback(async (rideId) => {
    if (!rideId || suggestions[rideId]) return suggestions[rideId] || [];
    try {
      const { data } = await apiClient.get(`/kaizen/offers/${rideId}/suggested-providers`);
      const rows = Array.isArray(data) ? data : [];
      setSuggestions((p) => ({ ...p, [rideId]: rows }));
      return rows;
    } catch {
      setSuggestions((p) => ({ ...p, [rideId]: [] }));
      return [];
    }
  }, [suggestions]);

  return { offers, fetchedAt, loading, busy, suggestions, accept, decline, loadSuggestions, refetch: load };
}

/** Extract stable ride id from the offer document (strips `kaizen-` prefix). */
export function offerRideId(offer) {
  if (!offer) return null;
  const kid = offer.kaizen_offer_id || "";
  if (kid.startsWith("kaizen-")) return kid.slice(7);
  return offer.ride_id || offer.offer?.ride_id || null;
}

/** Age of an offer in seconds since the ingest agent last saw it. */
export function offerAgeSeconds(offer) {
  const ts = offer?.last_seen_at || offer?.ingested_at;
  if (!ts) return null;
  const t = new Date(ts).getTime();
  if (!Number.isFinite(t)) return null;
  return Math.max(0, Math.round((Date.now() - t) / 1000));
}
